import React from 'react';
import ReactDOM from 'react-dom';
import Mathjax from './Mathjax';

function getTooltip(tip) {
    if (typeof tip === "string") {
        return tip.split("\n").map(function (item, idx) {
            return (
                <span key={idx}>
                    {idx > 0 && <br />}
                    <Mathjax value={item} />
                </span>
            );
        });
    } else {
        return tip;
    }
}

// Extract text content for aria-label (handle both string and React element tips)
function getTipText(tip) {
    if (typeof tip === "string") {
        return tip;
    }
    // For React elements, try to extract text content
    // This is a fallback - ideally tips should be strings
    return '';
}

export function ToolTipModal({ tip, offset, position, transform, theme, tooltipId, onMouseEnter, onMouseLeave }) {
    if (tip) {
        var pickerCss = {
            position: 'fixed',
            zIndex: '1000',
            maxWidth: '350px',
        };

        if (transform) {
            pickerCss.transform =
                transform === 'cy' ? 'translateY(-50%)' : 'translateX(-50%)';
        }

        switch (position) {
            case 'lt':
                pickerCss.right = window.innerWidth - offset.x;
                pickerCss.bottom = window.innerHeight - offset.y;
                break;
            case 'lb':
                pickerCss.right = window.innerWidth - offset.x;
                pickerCss.top = offset.y;
                break;
            case 'rt':
                pickerCss.left = offset.x;
                pickerCss.bottom = window.innerHeight - offset.y;
                break;
            case 'rb':
            default:
                pickerCss.left = offset.x;
                pickerCss.top = offset.y;
                break;
        }

        const tipText = getTipText(tip);

        return (
            <div
                className={'tde-theme-' + theme}
                style={pickerCss}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                <div className="tde-tooltip" id={tooltipId} aria-label={tipText} role="tooltip" tabIndex={-1}>
                    {getTooltip(tip)}
                </div>
            </div>
        );
    } else {
        return <div></div>;
    }
}

class ToolTip extends React.Component {
    constructor(props) {
        super(props);
        this.isHoveringTrigger = false;
        this.isHoveringTooltip = false;
        this.dismissTimeout = null;
        // Generate unique ID for this tooltip instance
        this.tooltipId = `tde-tooltip-${Math.random().toString(36).substr(2, 9)}`;
    }

    componentDidMount() {
        this.wrapperElement = document.createElement('div');
        this.wrapperElement.setAttribute('class', 'tde-tooltip-wrapper');
        this.wrapperElement.setAttribute('role', 'tooltip');
        this.wrapperElement.setAttribute('id', this.tooltipId);
        this.wrapperElement.setAttribute('tabIndex', '-1');
        document.body.appendChild(this.wrapperElement);
    }
    componentWillUnmount() {
        clearTimeout(this.dismissTimeout);
        this._safeUnmount(true);
    }

    renderToolTip = e => {
        const rect = e.target.getBoundingClientRect();
        let top;
        let left;
        let transform = 'cx';
        let position = 'rb';
        if (this.props.dir === 'r') {
            top = rect.y + rect.height / 2;
            left = rect.x + rect.width + 10;
            transform = 'cy';
        } else if (this.props.dir === 't') {
            top = rect.y - 10;
            left = rect.x + rect.width / 2;
            position = 'rt';
        } else {
            top = rect.y + rect.height + 10;
            left = rect.x + rect.width / 2;
        }

        const tipText = getTipText(this.props.tip);

        // Set aria-label on wrapper element
        if (tipText) {
            this.wrapperElement.setAttribute('aria-label', tipText);
        }

        const toolTipEle = React.createElement(ToolTipModal, {
            offset: {
                x: left,
                y: top,
            },
            transform,
            position: position,
            tip: this.props.tip,
            theme: this.props.Dyn.preferences.getTheme(),
            tooltipId: this.tooltipId,
            onMouseEnter: this.tooltipMouseEnter,
            onMouseLeave: this.tooltipMouseLeave,
        })

        clearTimeout( this.unmountTimeout );
        if(this.toolTipWrapperEleRoot ) {
            this.toolTipWrapperEleRoot.render(
                toolTipEle
            );
        } else {
            this.toolTipWrapperEleRoot = ReactDOM.createRoot(this.wrapperElement);
            this.toolTipWrapperEleRoot.render(
                toolTipEle
            );
        }
    }

    mouseEnter = (e, childMouseEnter) => {
        clearTimeout(this.dismissTimeout);
        this.isHoveringTrigger = true;
        this.renderToolTip(e);
        childMouseEnter && childMouseEnter(e);
    };

    mouseLeave = (e, childMouseLeave) => {
        this.isHoveringTrigger = false;
        this.scheduleTooltipDismissal();
        childMouseLeave && childMouseLeave(e);
    };

    tooltipMouseEnter = () => {
        clearTimeout(this.dismissTimeout);
        this.isHoveringTooltip = true;
    };

    tooltipMouseLeave = () => {
        this.isHoveringTooltip = false;
        this.scheduleTooltipDismissal();
    };

    scheduleTooltipDismissal = () => {
        clearTimeout(this.dismissTimeout);

        this.dismissTimeout = setTimeout(() => {
            if (!this.isHoveringTrigger && !this.isHoveringTooltip) {
                this._safeUnmount();
            }
        }, 200);
    };

    onBlur = (e, childOnBlur) => {
        this.isHoveringTrigger = false;
        this.isHoveringTooltip = false;
        clearTimeout(this.dismissTimeout);
        this._safeUnmount();
        childOnBlur && childOnBlur(e);
    };

    _safeUnmount = (canRemoveWrapperEle) => {
        // remove the tooltip, with time delay to avoid "warning:
        //      Attempted to synchronously unmount a root while React was already rendering.
        //      React cannot finish unmounting the root until the current render has completed, which may lead to a race condition."
        this.unmountTimeout = setTimeout(() => {
            this.toolTipWrapperEleRoot?.unmount();
            this.toolTipWrapperEleRoot = null;
            if(canRemoveWrapperEle) {
                document.body.removeChild(this.wrapperElement);
            }
        });
    }

    onFocus = (e, childOnFocus) => {
        clearTimeout(this.dismissTimeout);
        this.isHoveringTrigger = true;
        this.renderToolTip(e);
        childOnFocus && childOnFocus();
    };

    onKeyDown = (e, childKeyDown) => {
        if (e.keyCode === 27) { // ESC key
          this.onBlur()
        }
        childKeyDown && childKeyDown(e);
    }

    render() {
        const childKeyDown = this.props.children.props.onKeyDown;
        const childMouseEnter= this.props.children.props.onMouseEnter;
        const childMouseLeave = this.props.children.props.onMouseLeave;
        const childOnBlur = this.props.children.props.onBlur;
        const childOnFocus = this.props.children.props.onFocus;
        // Enable tooltip only for touch devices
        if (this.props.Dyn) {
            const props = {
                tabIndex: 0,
                'aria-describedby': this.tooltipId,
                onKeyDown: (e) => { this.onKeyDown(e, childKeyDown); },
                onFocus: (e) => { this.onFocus(e, childOnFocus); },
                onBlur: (e) => { this.onBlur(e, childOnBlur); },
                onMouseEnter: (e) => { this.mouseEnter(e, childMouseEnter); },
                onMouseLeave: (e) => { this.mouseLeave(e, childMouseLeave); }
            };
            if(this.props.follow) {
                props.onMouseMove = this.mouseEnter;
            }

            const children = React.cloneElement(this.props.children, props);
            return <React.Fragment>{children}</React.Fragment>;
        } else {
            return <React.Fragment>{this.props.children}</React.Fragment>;
        }
    }
}

export default ToolTip;
